import { array } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import $ from 'jquery';

import type { PopupListItem, Service } from 'webapp-types';
import { BillingFeature, EmblemType, PopupEvent, TemplateObjectType } from 'webapp/enums/enums';
import { TrackingEvent } from 'webapp/enums/tracking';
import { copyToClipboard, isAlive } from 'webapp/utils/common';

import joinClasses from 'webapp/helpers/join-classes';
import didInsert from 'webapp/modifiers/did-insert';
import visibleCheckItem from 'webapp/modifiers/visible-check-item';
import BillingLimitationWarningComponent from 'webapp/components/billing/limitation-warning';
import BoardImagePreviewComponent from 'webapp/components/board/image-preview';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import EditableModelPropertyComponent from 'webapp/components/editable-model-property';
import FormCheckboxComponent from 'webapp/components/form/checkbox';
import SubmitButtonComponent from 'webapp/components/submit-button';
import TagContainerComponent from 'webapp/components/tag-container';
import ToolTipComponent from 'webapp/components/tool-tip';
import UsedSourcesListComponent from 'webapp/components/used-sources-list';

import type BatchSelect from 'webapp/models-custom/batch-select';
import Board from 'webapp/models-custom/board';
import BoardLoop from 'webapp/models-custom/board-loop';
import Report from 'webapp/models-custom/report';
import type Template from 'webapp/models-custom/template';
import type SpaceRecord from 'webapp/models/space';

type TargetModel = Board | Report | BoardLoop;
interface Args {
	model: Template;
	batchSelect?: BatchSelect<Template>;
	onUseTemplate: (model: Template) => void;
	onPreviewTemplate: (model: Template) => void;
	isVisibleOnScreen?: boolean;
	targetModel?: TargetModel;
	hideSort?: boolean;
	onEditTemplate?: (model: TargetModel) => void;
	onDeleteTemplate?: (model: TargetModel) => void;
	onOpenTemplatePermissions?: (model: Board) => void;
}

export type Action =
	| 'openTemplatePermissions'
	| 'deleteTemplate'
	| 'editTemplate'
	| 'editBoardLoopTemplate'
	| 'copyTemplatePublicUrl';
export type Option = PopupListItem<Action>;

export default class TemplatesGridItemComponent extends Component<Args> {
	@service('popup') declare popupService: Service.Popup;
	@service('model-manager/templates') declare mmTemplatesService: Service.ModelManager.Templates;
	@service('model-manager/spaces') declare mmSpacesService: Service.ModelManager.Spaces;
	@service('model-manager/board-loops')
	declare mmBoardLoopsService: Service.ModelManager.BoardLoops;
	@service('billing') declare billingService: Service.Billing;
	@service('account') declare accountService: Service.Account;
	@service('model-manager/boards') declare mmBoardsService: Service.ModelManager.Boards;
	@service('model-manager/reports') declare mmReportsService: Service.ModelManager.Reports;
	@service('infolayer') declare infolayerService: Service.Infolayer;

	@tracked isOverlayActive = false;
	@tracked isVisibleOnScreen;

	node: HTMLElement | null = null;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		this.isVisibleOnScreen = this.args.isVisibleOnScreen ?? false;
	}

	get canEdit(): boolean {
		return !!this.args.targetModel?.canCurrentUserEdit && !this.accountService.isDemoAccount;
	}

	get emblemTypeName(): string {
		if (this.args.targetModel?.model.newBadge) {
			return EmblemType.NEW;
		} else if (this.isAccountTemplate) {
			return EmblemType.ACCOUNT_TEMPLATE;
		}

		return EmblemType.PUBLIC_TEMPLATE;
	}

	get additionalOptions(): Option[] {
		const targetModel = this.args.targetModel;
		const options: Option[] = [];

		if (!(targetModel instanceof Report)) {
			options.push({
				icon: 'Link',
				text: 'Copy public URL',
				action: 'copyTemplatePublicUrl',
			});
		}

		if (targetModel?.canCurrentUserEdit) {
			if (targetModel instanceof BoardLoop && targetModel.canEditPermissions) {
				options.push({
					icon: 'Pencil',
					text: 'Edit loop',
					action: 'editBoardLoopTemplate',
				});
			}

			if (
				(targetModel instanceof Board || targetModel instanceof BoardLoop) &&
				targetModel.canEditPermissions
			) {
				options.push({
					icon: 'UserAccess',
					text: 'User access',
					action: 'openTemplatePermissions',
				});
			}

			options.push({
				class: 'delete',
				icon: 'Trash',
				text: 'Delete',
				action: 'deleteTemplate',
			});
		}

		return options;
	}

	// This getter handles filter sort for both object types of account templates (with taking objectId)
	// grid is shown according to board/report model and not templates like on public templates pages
	get dataId(): number {
		const model = this.args.model;

		return this.args.targetModel ? model.objectId : model.numericId!;
	}

	// ----------- Account Templates stuff -----------

	get isAccountTemplate(): boolean {
		return !this.args.model.isPublic;
	}

	get spaceRecord(): SpaceRecord | null {
		if (!this.isAccountTemplate) {
			return null;
		}

		let spaceId: number | undefined, ownerId: number | undefined | null;
		const targetModel = this.args.targetModel;
		if (targetModel) {
			spaceId = targetModel.spaceId;
			ownerId = targetModel instanceof Board ? targetModel.createdById : targetModel.ownerId;
		} else {
			const objectType = this.args.model.objectType,
				objectId = this.args.model.objectId;

			if (objectType === TemplateObjectType.REPORT) {
				const report = this.mmReportsService.findModel(objectId.toString());

				if (report) {
					spaceId = report.spaceId;
					ownerId = report.ownerId;
				}
			} else {
				const board = this.mmBoardsService.findModel(objectId.toString());

				if (board) {
					spaceId = board.spaceId;
					ownerId = board.createdById;
				}
			}
		}

		if (spaceId === ownerId) {
			return null;
		} else {
			return (
				this.mmSpacesService.otherSpaceRecords.find(
					(item) => item.id === String(spaceId),
				) || null
			);
		}
	}

	get creatorName(): string {
		return this.spaceRecord
			? this.spaceRecord.companyName
			: this.args.model.createdBy.name || '';
	}

	get creatorAvatar(): string {
		return this.args.model.createdBy.avatar || ''; // space doesn't have avatar
	}

	get isAvatarOrInitialsShown(): boolean {
		return !!this.creatorName || !!this.creatorAvatar;
	}

	get isPrivate() {
		const target = this.args.targetModel;
		return (target instanceof Board || target instanceof BoardLoop) && target.isPrivate;
	}

	// ----------- Board Account Templates stuff -----------

	// Giving tooltip unique identifier that we can
	// later close specific instance of tooltip -
	// otherwise tooltip is over board permissions popup
	get privateTooltipClass(): string {
		return `private-${this.args.model.id}`;
	}

	get privateTooltipText(): JQuery<HTMLElement> {
		const text =
				'<p>This account template is set to private and is only visible to you</p><a role="button">Change user access</a>',
			el = $(`<div class="text-center">${text}</div>`);

		el.find('a').on('click', () => {
			this.openTemplatePermissions();

			const instance = $.tooltipster.instances(`.${this.privateTooltipClass}`);

			if (instance.length) {
				instance[0]!.close();
			}

			return false;
		});

		return el;
	}

	// ----------- Report Templates stuff -----------

	get isReportTemplate(): boolean {
		return this.args.model.objectType === TemplateObjectType.REPORT;
	}

	// ----------- Board Loop Templates stuff -----------

	get boardCount(): number {
		const targetModel = this.args.targetModel;
		const model = targetModel instanceof BoardLoop ? targetModel : this.args.model;
		return model.boardIds.length;
	}

	get isBoardLoopTemplate(): boolean {
		return this.args.model.objectType === TemplateObjectType.BOARD_LOOP;
	}

	get lockedFeature(): string {
		if (
			this.isReportTemplate &&
			!this.billingService.checkIfCan(BillingFeature.REPORTS_BASIC)
		) {
			return BillingFeature.REPORTS_BASIC;
		}
		if (this.isAccountTemplate && !this.billingService.accountTemplatesEnabled) {
			return BillingFeature.ACCOUNT_TEMPLATES;
		}

		return '';
	}

	get isSelected(): boolean {
		const { batchSelect, model } = this.args;
		return !!batchSelect?.isItemSelected(model);
	}

	get showBatchSelectCheckbox(): boolean {
		return !!this.args.batchSelect && this.canEdit;
	}

	@action
	selectItem(selected: boolean): void {
		const { model, batchSelect } = this.args;
		batchSelect?.changeSelection(model, selected);
	}

	@action
	openAdditionalOptions(): void {
		const node = this.node;

		if (!node) {
			return;
		}

		if (!this.isOverlayActive) {
			const attachTo = node.querySelector<HTMLElement>('.context');
			const popup = this.popupService.createListPopup(attachTo);

			if (popup) {
				popup.open({
					itemsList: this.additionalOptions,
					doAction: (actionName: string) => {
						this[actionName as Action]();
					},
				});

				popup.on(PopupEvent.CLOSED, this, () => {
					if (isAlive(this)) {
						this.isOverlayActive = false;
					}
				});

				this.isOverlayActive = true;
			}
		}
	}

	@action
	useTemplate(): void {
		if (this.accountService.handleDemo()) {
			return;
		}

		if (this.lockedFeature) {
			this.billingService.handleLimitations(this.lockedFeature as BillingFeature);

			return;
		}

		this.args.onUseTemplate(this.args.model);
	}

	@action
	previewTemplate(): void {
		this.args.onPreviewTemplate(this.args.model);
	}

	@action
	openTemplatePermissions(): void {
		if (this.args.onOpenTemplatePermissions) {
			this.args.onOpenTemplatePermissions(this.args.targetModel as Board);
		}
	}

	@action
	deleteTemplate(): void {
		this.args.batchSelect?.unselectAll();
		if (this.args.onDeleteTemplate && this.args.targetModel) {
			this.args.onDeleteTemplate(this.args.targetModel);
		}
	}

	@action
	copyTemplatePublicUrl(): void {
		const copyPublicUrl = this.args.targetModel?.getDatawallUrlWithPreviewHash() || '';

		if (!copyPublicUrl) {
			this.infolayerService.error(
				'Something went wrong. Could not copy public URL for this template.',
			);
		} else {
			copyToClipboard(copyPublicUrl);
			this.infolayerService.ok('Public URL has been been copied to clipboard!');
		}
	}

	@action
	editTemplate(): void {
		if (this.lockedFeature) {
			this.billingService.handleLimitations(this.lockedFeature as BillingFeature);

			return;
		}

		if (this.args.onEditTemplate && this.args.targetModel) {
			this.mmTemplatesService.trackEvent(this.args.model, TrackingEvent.TEMPLATE_EDIT);
			this.args.onEditTemplate(this.args.targetModel);
		}
	}

	@action
	editBoardLoopTemplate(): void {
		if (this.args.targetModel instanceof BoardLoop) {
			this.mmBoardLoopsService.openEditPopup(this.args.targetModel);
		}
	}

	@action
	visibleChanged(isVisible: boolean): void {
		this.isVisibleOnScreen = isVisible;
	}

	@action
	setupView(node: HTMLElement): void {
		this.node = node;
	}
	<template>
		<li
			class={{joinClasses
				(array
					"card"
					"card--preview"
					"flex-item"
					"filter-sort-item"
					@model.animationClass
					(if this.isOverlayActive "card--show-overlay")
					(if @hideSort "hideSort")
					(if this.isSelected "selected")
				)
			}}
			data-id={{this.dataId}}
			data-template-id={{@model.id}}
			{{! used for sorting board/report templates }}
			{{didInsert this.setupView}}
		>
			<BoardImagePreviewComponent
				@image={{@model.image}}
				@color={{@targetModel.color}}
				@theme={{@targetModel.bgTheme}}
				@isVisibleOnScreen={{this.isVisibleOnScreen}}
				@isLazyLoaded={{true}}
			/>

			{{#if this.isVisibleOnScreen}}

				<div class="overlay">

					<div class="overlay-bg"></div>

					{{#if @targetModel.canCurrentUserEdit}}
						<div class="drag" data-id={{@model.objectId}}>
							<CommonUiIconComponent @name="ArrowsMove" @size="md" />
						</div>
					{{/if}}

					<div class="wrap">
						<div class="actions">
							<div class="actions__top {{if this.lockedFeature 'with-warning'}}">
								<SubmitButtonComponent
									@text="Preview"
									@extraClass="previewbutton"
									@onTrigger={{this.previewTemplate}}
									@noDelay={{true}}
									@theme="blue-outlined"
								/>

								{{#if @targetModel.canCurrentUserEdit}}
									<a
										role="button"
										class="button rippler-default theme-grey-outlined size-normal rippler editbutton"
										{{on "click" this.editTemplate}}
									>
										<CommonUiIconComponent @name="Pencil" @size="sm" />
										Edit
										{{#if this.lockedFeature}}
											<BillingLimitationWarningComponent />
										{{/if}}
									</a>
								{{/if}}
							</div>
							<div class="actions__bottom">
								<a
									role="button"
									class="button rippler-default theme-blue size-normal rippler"
									{{on "click" this.useTemplate}}
								>
									Use template
									{{#if this.lockedFeature}}
										<BillingLimitationWarningComponent />
									{{/if}}
								</a>
							</div>
						</div>
					</div>

				</div>

				<div class="top">
					<div class="top__left">

						{{#if this.showBatchSelectCheckbox}}
							<div class="checkbox-wrapper">
								<FormCheckboxComponent
									@name="templates-grid-item"
									@selected={{this.isSelected}}
									@theme="blue"
									@onChange={{this.selectItem}}
								/>
							</div>
						{{/if}}

						{{#if this.isPrivate}}
							<div role="button" class="private {{this.privateTooltipClass}}">
								<CommonUiIconComponent @name="Lock" @size="xs" />
								<ToolTipComponent
									@text={{this.privateTooltipText}}
									@interactive={{true}}
								/>
							</div>
						{{/if}}

						{{#if this.emblemTypeName}}
							<div class="emblem emblem--{{this.emblemTypeName}}"></div>
						{{/if}}

						{{#if this.isBoardLoopTemplate}}
							<div class="emblem emblem--loop-template">
								{{this.boardCount}}
								<CommonUiIconComponent @name="Loop" @size="sm" />
							</div>
						{{/if}}

					</div>

					<div class="top__right">
						{{#if @targetModel.canCurrentUserEdit}}
							<div
								role="button"
								class="context"
								{{on "click" this.openAdditionalOptions}}
							>
								<CommonUiIconComponent @name="Ellipsis" @size="lg" />
							</div>
						{{/if}}
					</div>
				</div>

				<div class="info">
					<div class="anti-stretch">
						<EditableModelPropertyComponent
							@model={{@model}}
							@canEdit={{this.canEdit}}
						/>

						{{#if @model.isPublic}}
							<br />
							<EditableModelPropertyComponent
								@model={{@model}}
								@canEdit={{this.canEdit}}
								@editableProperty="description"
								@className="description"
							/>
						{{/if}}

					</div>
				</div>

				<div class="bottom">
					<UsedSourcesListComponent @sources={{@model.sourcesUsedList}} @size="sm" />
					{{#if this.isAccountTemplate}}
						{{#if @targetModel.canCurrentUserEdit}}
							<TagContainerComponent @model={{@targetModel}} />
						{{else}}
							<div class="creator">
								<span>
									<CommonUiIconComponent @name="UserManagement" @size="sm" />
									{{this.creatorName}}
									<ToolTipComponent @text={{this.creatorName}} />
								</span>
							</div>
						{{/if}}
					{{else}}
						{{#if this.isReportTemplate}}
							<div>{{@model.images.length}} slides</div>
						{{/if}}
					{{/if}}
				</div>

			{{else}}
				<div class="info" {{visibleCheckItem this.visibleChanged false}}>
					<div class="anti-stretch">&nbsp;</div>
				</div>
			{{/if}}
		</li>
	</template>
}
